/* eslint-disable max-lines */
import {
  IS_EPIC_INTEGRATION_ENABLED,
  IS_MIND_BODY_INTEGRATION_ENABLED,
} from '@/features/flags/legacy-flags';
import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { EHRProvider, IntegrationOption } from '@heidi/schema/ml-scribe';
import { atom } from 'jotai';

// Integrations that are available on SDK widget
export const integrationsAvailableOnSDK = [
  EHRProvider.Halaxy,
  EHRProvider.Medical_Director,
  EHRProvider.MediRecords,
  EHRProvider.Semble,
  EHRProvider.Elixir,
  EHRProvider.Vim,
];

// Partial: the EHRProvider enum lists every backend-known EMR, far more than
// the subset with a Heidi setup guide. Consumers guard on presence.
export const IntegrationGuidePaths: Partial<Record<EHRProvider, string>> = {
  [EHRProvider.Halaxy]: 'articles/10094086-integrate-with-halaxy',
  [EHRProvider.Medical_Director]:
    'articles/9928949-medical-director-log-in-sign-up-process',
  [EHRProvider.MediRecords]: 'articles/10101429-using-the-heidi-widget-with-medi-records',
  [EHRProvider.Other]:
    'articles/9914512-heidi-widget-log-in-sign-up-and-upgrade-subscription-guide',
  // No SDK available for these integrations
  [EHRProvider.Best_Practice]: 'articles/9382109-integrating-with-best-practice',
  [EHRProvider.Cliniko]: 'articles/9576012-cliniko-integration-setup-use',
  [EHRProvider.Mindbody]: '',
  [EHRProvider.Zedmed]: '',
  [EHRProvider.Genie]: '',
  [EHRProvider.Genie_Web]: '',
  [EHRProvider.Pracsoft]: '',
  [EHRProvider.Medtech]: '',
  [EHRProvider.Clinic_to_Cloud]: '',
  [EHRProvider.MedicalMinded]: '',
  [EHRProvider.Epic_Systems]: '',
  [EHRProvider.Cerner]: '',
  [EHRProvider.Cerner_EHR_Launch]: '',
  [EHRProvider.Cerbo]: '',
  [EHRProvider.athenahealth]: '',
  [EHRProvider.eClinicalWorks]: '',
  [EHRProvider.Allscripts]: '',
  [EHRProvider.NextGen_Healthcare]: '',
  [EHRProvider.DrChrono]: '',
  [EHRProvider.Greenway_Health]: '',
  [EHRProvider.CureMD]: '',
  [EHRProvider.Kareo]: '',
  [EHRProvider.AdvancedMD]: '',
  [EHRProvider.Practice_Fusion]: '',
  [EHRProvider.Aprima]: '',
  [EHRProvider.AmazingCharts]: '',
  [EHRProvider.ChartLogic]: '',
  [EHRProvider.Modernizing_Medicine]: '',
  [EHRProvider.Compulink]: '',
  [EHRProvider.Nextech]: '',
  [EHRProvider.RevolutionEHR]: '',
  [EHRProvider.CareCloud]: '',
  [EHRProvider.EMIS]: '',
  [EHRProvider.TPP_SystmOne]: '',
  [EHRProvider.Vision_Health]: '',
  [EHRProvider.Shexie]: '',
  [EHRProvider.IntakeQ]: '',
  [EHRProvider.Mediclinic]: '',
  [EHRProvider.Power_Diary]: '',
  [EHRProvider.TherapyNotes]: '',
  [EHRProvider.SimplePractice]: '',
  [EHRProvider.MyOutcomes]: '',
  [EHRProvider.Nookal]: '',
  [EHRProvider.TheraNest]: '',
  [EHRProvider.Magentus_Genie_]: '',
  [EHRProvider.Orion_Health]: '',
  [EHRProvider.InterSystems]: '',
  [EHRProvider.Access_Rio]: '',
  [EHRProvider.iaptus_by_Mayden]: '',
  [EHRProvider.Meditech_Expanse]: '',
  [EHRProvider.WebPT]: '',
  [EHRProvider.Avaros]: '',
  [EHRProvider.BlueprintOMS]: '',
  [EHRProvider.BodySite]: '',
  [EHRProvider.Carebit]: '',
  [EHRProvider.Charm]: '',
  [EHRProvider.Client_Care]: '',
  [EHRProvider.Cornerstone]: '',
  [EHRProvider.Counselear]: '',
  [EHRProvider.Counsol]: '',
  [EHRProvider.e_MDS]: '',
  [EHRProvider.Elation]: '',
  [EHRProvider.Galaxy]: '',
  [EHRProvider.Gensolve]: '',
  [EHRProvider.Gentu]: '',
  [EHRProvider.Google_Calendar]: '',
  [EHRProvider.Headway]: '',
  [EHRProvider.Healthie]: '',
  [EHRProvider.HealthQuest]: '',
  [EHRProvider.Helix]: '',
  [EHRProvider.Indici]: '',
  [EHRProvider.JunoOscar]: '',
  [EHRProvider.MDLand]: '',
  [EHRProvider.MedServ_RPM]: '',
  [EHRProvider.Micro_Quest]: '',
  [EHRProvider.Midex_Pro]: '',
  [EHRProvider.MyOsteopathy]: '',
  [EHRProvider.MyVirtualCare]: '',
  [EHRProvider.nCAS_EHR_Launch]: '',
  [EHRProvider.Nephro]: '',
  [EHRProvider.Nephro7]: '',
  [EHRProvider.Notability]: '',
  [EHRProvider.Optimantra]: '',
  [EHRProvider.Oscar_Pro]: '',
  [EHRProvider.Osmind]: '',
  [EHRProvider.Outsmart]: '',
  [EHRProvider.Owl]: '',
  [EHRProvider.Pabau]: '',
  [EHRProvider.Patient_Now]: '',
  [EHRProvider.PPMS]: '',
  [EHRProvider.PracSuite]: '',
  [EHRProvider.Practice_Better]: '',
  [EHRProvider.Praxis]: '',
  [EHRProvider.Prognocis]: '',
  [EHRProvider.Renesan]: '',
  [EHRProvider.Rx]: '',
  [EHRProvider.Semble]: '',
  [EHRProvider.Splose]: '',
  [EHRProvider.Telus]: '',
  [EHRProvider.Telus_CHR_EHR_Launch]: '',
  [EHRProvider.TextFlow]: '',
  [EHRProvider.Therabill]: '',
  [EHRProvider.Therabyte]: '',
  [EHRProvider.Therapymate]: '',
  [EHRProvider.Timely]: '',
  [EHRProvider.Unified_Practice]: '',
  [EHRProvider.UroChart_EHR]: '',
  [EHRProvider.Veradigm_EHR]: '',
  [EHRProvider.VIP]: '',
  [EHRProvider.WriteUpp]: '',
  [EHRProvider.Xestro]: '',
  [EHRProvider.ZHealthEHR]: '',
  [EHRProvider.Elixir]: '',
  [EHRProvider.Heidi]: '',
  [EHRProvider.Accuro]: '',
  [EHRProvider.Adoreal]: '',
  [EHRProvider.Akute_Health]: '',
  [EHRProvider.Alaya_Care]: '',
  [EHRProvider.Automated_Medical_Assistant]: '',
  [EHRProvider.Better_Clinics]: '',
  [EHRProvider.Big_Picture_Medical]: '',
  [EHRProvider.Biocanic]: '',
  [EHRProvider.Blueprint_Solutions]: '',
  [EHRProvider.CareStack]: '',
  [EHRProvider.ClaimPower]: '',
  [EHRProvider.Genius]: '',
  [EHRProvider.Hefficient]: '',
  [EHRProvider.InstantScripts]: '',
  [EHRProvider.Keiser]: '',
  [EHRProvider.Keyreply]: '',
  [EHRProvider.Magentus]: '',
  [EHRProvider.MediOffice]: '',
  [EHRProvider.Practice_Hub]: '',
  [EHRProvider.PracticeQ]: '',
  [EHRProvider.Renvio]: '',
  [EHRProvider.Sound_Business]: '',
  [EHRProvider.StrongroomAI]: '',
  [EHRProvider.Suvera]: '',
  [EHRProvider.TalkNow]: '',
  [EHRProvider.USW]: '',
  [EHRProvider.Weva]: '',
  [EHRProvider.WRS]: '',
  [EHRProvider.xyCare]: '',
  [EHRProvider.ALBIS]: '',
  [EHRProvider.Anderes_System]: '',
  [EHRProvider.ARZT_2000]: '',
  [EHRProvider.Avelios]: '',
  [EHRProvider.CGM_CLINICAL]: '',
  [EHRProvider.CGM_M1_PRO]: '',
  [EHRProvider.CGM_MEDICO]: '',
  [EHRProvider.CGM_MEDISTAR]: '',
  [EHRProvider.ClinicCentre]: '',
  [EHRProvider.ClinicCentre_Billing]: '',
  [EHRProvider.Data_AL]: '',
  [EHRProvider.Doctorly]: '',
  [EHRProvider.DURIA]: '',
  [EHRProvider.DURIA2]: '',
  [EHRProvider.Elefant]: '',
  [EHRProvider.Epikur]: '',
  [EHRProvider.esQlab_online]: '',
  [EHRProvider.EVA]: '',
  [EHRProvider.EVIDENT]: '',
  [EHRProvider.FIDUS]: '',
  [EHRProvider.IMedOne]: '',
  [EHRProvider.IMP_System]: '',
  [EHRProvider.inSuite]: '',
  [EHRProvider.InterARZT]: '',
  [EHRProvider.KiWi]: '',
  [EHRProvider.medatixx]: '',
  [EHRProvider.MEDAVIS_RIS]: '',
  [EHRProvider.Medical_Office]: '',
  [EHRProvider.medico]: '',
  [EHRProvider.MEDYS]: '',
  [EHRProvider.M_KIS]: '',
  [EHRProvider.Nexsoft]: '',
  [EHRProvider.NEXUS_KIS]: '',
  [EHRProvider.ORBIS]: '',
  [EHRProvider.ORBIS_U]: '',
  [EHRProvider.PegaMed]: '',
  [EHRProvider.Praxis_Programm]: '',
  [EHRProvider.PROFIMED]: '',
  [EHRProvider.Pro_Medico]: '',
  [EHRProvider.PsychoDat]: '',
  [EHRProvider.psyprax]: '',
  [EHRProvider.PsyPrax_KIS]: '',
  [EHRProvider.QUINCY_WIN]: '',
  [EHRProvider.RED_Medical]: '',
  [EHRProvider.RescuePro]: '',
  [EHRProvider.S3_Win]: '',
  [EHRProvider.SAP_IS_H_i_s_h_med]: '',
  [EHRProvider.SMARTY]: '',
  [EHRProvider.T2med]: '',
  [EHRProvider.tomedo]: '',
  [EHRProvider.TURBOMED]: '',
  [EHRProvider.vianova_KIS]: '',
  [EHRProvider.XCare]: '',
  [EHRProvider.x_comfort]: '',
  [EHRProvider.x_concept]: '',
  [EHRProvider.x_isynet]: '',
  [EHRProvider.Vim]: '',
  [EHRProvider.Airrosti]: '',
  [EHRProvider.Hero_Health]: '',
  [EHRProvider.Healthdirect]: '',
  [EHRProvider.eClinicalWorks_EHR_Launch]: '',
  [EHRProvider.SGIMED]: '',
  [EHRProvider.xentro]: '',
  [EHRProvider.x_vianova]: '',
  [EHRProvider.Sandwell]: '',
  [EHRProvider.BIH]: '',
  [EHRProvider.KFSH]: '',
  [EHRProvider.EasternHealth]: '',
};

export const getIntegrationGuideUrl = (
  provider: EHRProvider,
  locale?: string
): string => {
  const path = IntegrationGuidePaths[provider];
  return path ? getHelpCenterUrl(path, locale) : '';
};

export const selectedIntegrationAtom = atom<EHRProvider>();
export const integrationMaxAttemptsReachedAtom = atom<boolean>(false);

export const useAvailableIntegrations = () => {
  const mindbodyIntegrationEnabled = IS_MIND_BODY_INTEGRATION_ENABLED();
  const epicIntegrationEnabled = IS_EPIC_INTEGRATION_ENABLED();

  return {
    availableIntegrations: [
      EHRProvider.Best_Practice,
      EHRProvider.Magentus,
      epicIntegrationEnabled && EHRProvider.Epic_Systems,
      mindbodyIntegrationEnabled && EHRProvider.Mindbody,
    ],
  };
};

export const SECTIONAL_WRITEBACK_ENABLED_EHR = [EHRProvider.athenahealth];

export const useIsSectionalWritebackEnabled = () => {
  const { integrationEhrProvider } = useIntegrationEhrInfo();

  return SECTIONAL_WRITEBACK_ENABLED_EHR.some((ehr) => ehr === integrationEhrProvider);
};

// TODO - Benjamin: Remove this once we removed deprecated integration field and move to ehr_provider
export const IntegrationOptionToEHRProviderMap: Record<IntegrationOption, EHRProvider> = {
  [IntegrationOption.athenahealth]: EHRProvider.athenahealth,
  [IntegrationOption.Nookal]: EHRProvider.Nookal,
  [IntegrationOption.Best_Practice]: EHRProvider.Best_Practice,
  [IntegrationOption.Cliniko]: EHRProvider.Cliniko,
  [IntegrationOption.Clinic_to_Cloud]: EHRProvider.Clinic_to_Cloud,
  [IntegrationOption.Cerner]: EHRProvider.Cerner,
  [IntegrationOption.Epic_Systems]: EHRProvider.Epic_Systems,
  [IntegrationOption.eClinicalWorks]: EHRProvider.eClinicalWorks,
  [IntegrationOption.Allscripts]: EHRProvider.Allscripts,
  [IntegrationOption.NextGen_Healthcare]: EHRProvider.NextGen_Healthcare,
  [IntegrationOption.DrChrono]: EHRProvider.DrChrono,
  [IntegrationOption.Greenway_Health]: EHRProvider.Greenway_Health,
  [IntegrationOption.CureMD]: EHRProvider.CureMD,
  [IntegrationOption.Kareo]: EHRProvider.Kareo,
  [IntegrationOption.AdvancedMD]: EHRProvider.AdvancedMD,
  [IntegrationOption.Practice_Fusion]: EHRProvider.Practice_Fusion,
  [IntegrationOption.Aprima]: EHRProvider.Aprima,
  [IntegrationOption.AmazingCharts]: EHRProvider.AmazingCharts,
  [IntegrationOption.ChartLogic]: EHRProvider.ChartLogic,
  [IntegrationOption.Modernizing_Medicine]: EHRProvider.Modernizing_Medicine,
  [IntegrationOption.MediRecords]: EHRProvider.MediRecords,
  [IntegrationOption.Medical_Director]: EHRProvider.Medical_Director,
  [IntegrationOption.Zedmed]: EHRProvider.Zedmed,
  [IntegrationOption.Genie]: EHRProvider.Genie,
  [IntegrationOption.Genie_Web]: EHRProvider.Genie_Web,
  [IntegrationOption.nCAS_EHR_Launch]: EHRProvider.nCAS_EHR_Launch,
  [IntegrationOption.Pracsoft]: EHRProvider.Pracsoft,
  [IntegrationOption.Medtech]: EHRProvider.Medtech,
  [IntegrationOption.MedicalMinded]: EHRProvider.MedicalMinded,
  [IntegrationOption.Compulink]: EHRProvider.Compulink,
  [IntegrationOption.Nextech]: EHRProvider.Nextech,
  [IntegrationOption.RevolutionEHR]: EHRProvider.RevolutionEHR,
  [IntegrationOption.CareCloud]: EHRProvider.CareCloud,
  [IntegrationOption.EMIS]: EHRProvider.EMIS,
  [IntegrationOption.TPP_SystmOne]: EHRProvider.TPP_SystmOne,
  [IntegrationOption.Vision_Health]: EHRProvider.Vision_Health,
  [IntegrationOption.Shexie]: EHRProvider.Shexie,
  [IntegrationOption.IntakeQ]: EHRProvider.IntakeQ,
  [IntegrationOption.Mediclinic]: EHRProvider.Mediclinic,
  [IntegrationOption.Power_Diary]: EHRProvider.Power_Diary,
  [IntegrationOption.TherapyNotes]: EHRProvider.TherapyNotes,
  [IntegrationOption.SimplePractice]: EHRProvider.SimplePractice,
  [IntegrationOption.MyOutcomes]: EHRProvider.MyOutcomes,
  [IntegrationOption.TheraNest]: EHRProvider.TheraNest,
  [IntegrationOption.Magentus_Genie_]: EHRProvider.Magentus_Genie_,
  [IntegrationOption.Orion_Health]: EHRProvider.Orion_Health,
  [IntegrationOption.InterSystems]: EHRProvider.InterSystems,
  [IntegrationOption.Access_Rio]: EHRProvider.Access_Rio,
  [IntegrationOption.iaptus_by_Mayden]: EHRProvider.iaptus_by_Mayden,
  [IntegrationOption.Meditech_Expanse]: EHRProvider.Meditech_Expanse,
  [IntegrationOption.WebPT]: EHRProvider.WebPT,
  [IntegrationOption.Assisted_Living]: EHRProvider.Other,
  [IntegrationOption.Avaros]: EHRProvider.Avaros,
  [IntegrationOption.BlueprintOMS]: EHRProvider.BlueprintOMS,
  [IntegrationOption.BodySite]: EHRProvider.BodySite,
  [IntegrationOption.Carebit]: EHRProvider.Carebit,
  [IntegrationOption.Charm]: EHRProvider.Charm,
  [IntegrationOption.Client_Care]: EHRProvider.Client_Care,
  [IntegrationOption.Cornerstone]: EHRProvider.Cornerstone,
  [IntegrationOption.Counselear]: EHRProvider.Counselear,
  [IntegrationOption.Counsol]: EHRProvider.Counsol,
  [IntegrationOption.e_MDS]: EHRProvider.e_MDS,
  [IntegrationOption.Elation]: EHRProvider.Elation,
  [IntegrationOption.Galaxy]: EHRProvider.Galaxy,
  [IntegrationOption.Gensolve]: EHRProvider.Gensolve,
  [IntegrationOption.Gentu]: EHRProvider.Gentu,
  [IntegrationOption.Halaxy]: EHRProvider.Halaxy,
  [IntegrationOption.Headway]: EHRProvider.Headway,
  [IntegrationOption.Healthie]: EHRProvider.Healthie,
  [IntegrationOption.HealthQuest]: EHRProvider.HealthQuest,
  [IntegrationOption.Helix]: EHRProvider.Helix,
  [IntegrationOption.Indici]: EHRProvider.Indici,
  [IntegrationOption.JunoOscar]: EHRProvider.JunoOscar,
  [IntegrationOption.MDLand]: EHRProvider.MDLand,
  [IntegrationOption.MedServ_RPM]: EHRProvider.MedServ_RPM,
  [IntegrationOption.Micro_Quest]: EHRProvider.Micro_Quest,
  [IntegrationOption.Midex_Pro]: EHRProvider.Midex_Pro,
  [IntegrationOption.MyOsteopathy]: EHRProvider.MyOsteopathy,
  [IntegrationOption.MyVirtualCare]: EHRProvider.MyVirtualCare,
  [IntegrationOption.Notability]: EHRProvider.Notability,
  [IntegrationOption.Optimantra]: EHRProvider.Optimantra,
  [IntegrationOption.Oscar_Pro]: EHRProvider.Oscar_Pro,
  [IntegrationOption.Osmind]: EHRProvider.Osmind,
  [IntegrationOption.Outsmart]: EHRProvider.Outsmart,
  [IntegrationOption.Owl]: EHRProvider.Owl,
  [IntegrationOption.Pabau]: EHRProvider.Pabau,
  [IntegrationOption.Patient_Now]: EHRProvider.Patient_Now,
  [IntegrationOption.PPMS]: EHRProvider.PPMS,
  [IntegrationOption.PracSuite]: EHRProvider.PracSuite,
  [IntegrationOption.Practice_Better]: EHRProvider.Practice_Better,
  [IntegrationOption.Praxis]: EHRProvider.Praxis,
  [IntegrationOption.Prognocis]: EHRProvider.Prognocis,
  [IntegrationOption.Renesan]: EHRProvider.Renesan,
  [IntegrationOption.Rx]: EHRProvider.Rx,
  [IntegrationOption.Semble]: EHRProvider.Semble,
  [IntegrationOption.Splose]: EHRProvider.Splose,
  [IntegrationOption.Telus]: EHRProvider.Telus,
  [IntegrationOption.TextFlow]: EHRProvider.TextFlow,
  [IntegrationOption.Therabill]: EHRProvider.Therabill,
  [IntegrationOption.Therabyte]: EHRProvider.Therabyte,
  [IntegrationOption.Therapymate]: EHRProvider.Therapymate,
  [IntegrationOption.Timely]: EHRProvider.Timely,
  [IntegrationOption.Unified_Practice]: EHRProvider.Unified_Practice,
  [IntegrationOption.UroChart_EHR]: EHRProvider.UroChart_EHR,
  [IntegrationOption.Veradigm_EHR]: EHRProvider.Veradigm_EHR,
  [IntegrationOption.VIP]: EHRProvider.VIP,
  [IntegrationOption.WriteUpp]: EHRProvider.WriteUpp,
  [IntegrationOption.Xestro]: EHRProvider.Xestro,
  [IntegrationOption.ZHealthEHR]: EHRProvider.ZHealthEHR,
  [IntegrationOption.Elixir]: EHRProvider.Elixir,
  [IntegrationOption.Other]: EHRProvider.Other,
  [IntegrationOption.Vim]: EHRProvider.Vim,
};
